import React from 'react';
import './InvoiceTemplate.css';

const EstimateTemplate = React.forwardRef(({ estimate, lineItems, customer, company }, ref) => {
  const formatCurrency = (amount) => `₹${amount.toFixed(2)}`;

  const formatDate = (dateValue, options = { year: 'numeric', month: 'short', day: 'numeric' }) => {
    if (!dateValue) return 'N/A';
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-GB', options);
  };

  const estimateDate = estimate.estimate_date
    ? formatDate(estimate.estimate_date, { year: 'numeric', month: 'long', day: 'numeric' })
    : formatDate(new Date(), { year: 'numeric', month: 'long', day: 'numeric' });

  const validUntil = estimate.validity_date
    ? formatDate(estimate.validity_date, { year: 'numeric', month: 'long', day: 'numeric' })
    : 'N/A';

  const timelineStart = estimate.timeline_start && estimate.timeline_start !== '0000-00-00'
    ? formatDate(estimate.timeline_start)
    : null;

  const timelineEnd = estimate.timeline_end && estimate.timeline_end !== '0000-00-00'
    ? formatDate(estimate.timeline_end)
    : null;

  const subtotal = lineItems.reduce((sum, item) => {
    const qty = parseFloat(item.quantity) || 0;
    const rate = parseFloat(item.unit_rate) || 0;
    return sum + (qty * rate);
  }, 0);

  const taxAmount = (subtotal * parseFloat(estimate.tax_percentage || 0)) / 100;
  const discountAmount = estimate.discount_type === 'percentage'
    ? (subtotal * parseFloat(estimate.discount_value || 0)) / 100
    : parseFloat(estimate.discount_value || 0);

  const total = subtotal + taxAmount - discountAmount;

  const getApprovalColor = (status) => {
    switch(status) {
      case 'pending_approval': return { bg: '#fef3c7', text: '#92400e' };
      case 'approved': return { bg: '#dcfce7', text: '#166534' };
      case 'rejected': return { bg: '#fee2e2', text: '#991b1b' };
      default: return { bg: '#e2e8f0', text: '#334155' };
    }
  };

  const approvalColor = getApprovalColor(estimate.approval_status);
  const customerName = customer?.name || estimate.contact_person || estimate.customer_name || 'Customer';
  const companyName = company.company || 'Your Company';
  const timelineText = timelineStart && timelineEnd ? `${timelineStart} to ${timelineEnd}` : 'Timeline to be finalized';

  return (
    <div ref={ref} style={{ backgroundColor: '#ffffff', color: '#0f172a', fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif', lineHeight: '1.5', padding: '36px' }}>
      <div style={{ border: '1px solid #dbeafe', borderRadius: '18px', overflow: 'hidden', boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08)' }}>
        <div style={{ background: 'linear-gradient(120deg, #0f172a 0%, #1d4ed8 100%)', color: '#f8fafc', padding: '24px 28px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '20px' }}>
            <div style={{ maxWidth: '70%' }}>
              <p style={{ margin: '0', fontSize: '11px', letterSpacing: '1.8px', textTransform: 'uppercase', color: '#bfdbfe', fontWeight: '700' }}>Project Budget Estimate</p>
              <h1 style={{ margin: '10px 0 4px 0', fontSize: '30px', lineHeight: '1.2', fontWeight: '800' }}>Estimate #{estimate.estimateNumber}</h1>
              <p style={{ margin: '0', fontSize: '13px', color: '#dbeafe' }}>Prepared for {customerName}</p>
            </div>

            <div style={{ textAlign: 'right' }}>
              {company.logo && (
                <img
                  src={company.logo}
                  alt="Company Logo"
                  style={{ width: '72px', height: '72px', objectFit: 'contain', marginLeft: 'auto', marginBottom: '10px', backgroundColor: 'rgba(255,255,255,0.1)', padding: '8px', borderRadius: '12px' }}
                />
              )}
              <span style={{ backgroundColor: approvalColor.bg, color: approvalColor.text, borderRadius: '999px', padding: '6px 12px', fontSize: '11px', fontWeight: '700', letterSpacing: '0.4px', textTransform: 'uppercase' }}>
                {String(estimate.approval_status || 'pending_approval').replace(/_/g, ' ')}
              </span>
            </div>
          </div>
        </div>

        <div style={{ padding: '24px 28px 28px 28px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '18px', marginBottom: '20px' }}>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: '14px', padding: '16px', backgroundColor: '#f8fafc' }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '11px', letterSpacing: '1.2px', textTransform: 'uppercase', color: '#64748b', fontWeight: '700' }}>Issued By</p>
              <p style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>{companyName}</p>
              <p style={{ margin: '0 0 2px 0', fontSize: '12px', color: '#475569' }}>{company.email || ''}</p>
              <p style={{ margin: '0 0 2px 0', fontSize: '12px', color: '#475569' }}>{company.phone || ''}</p>
              <p style={{ margin: '0', fontSize: '12px', color: '#475569' }}>{company.address || ''}</p>
            </div>

            <div style={{ border: '1px solid #e2e8f0', borderRadius: '14px', padding: '16px', backgroundColor: '#f8fafc' }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '11px', letterSpacing: '1.2px', textTransform: 'uppercase', color: '#64748b', fontWeight: '700' }}>Prepared For</p>
              <p style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>{customerName}</p>
              <p style={{ margin: '0 0 2px 0', fontSize: '12px', color: '#475569' }}>{estimate.client_email || customer?.email || ''}</p>
              <p style={{ margin: '0 0 2px 0', fontSize: '12px', color: '#475569' }}>{customer?.phone || estimate.customer_phone || ''}</p>
              <p style={{ margin: '0', fontSize: '12px', color: '#475569' }}>{customer?.address || ''}</p>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '12px', marginBottom: '20px' }}>
            <div style={{ border: '1px solid #dbeafe', borderRadius: '12px', padding: '12px', backgroundColor: '#eff6ff' }}>
              <p style={{ margin: '0', fontSize: '10px', color: '#1d4ed8', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Estimate Date</p>
              <p style={{ margin: '6px 0 0 0', fontSize: '13px', fontWeight: '700' }}>{estimateDate}</p>
            </div>
            <div style={{ border: '1px solid #dcfce7', borderRadius: '12px', padding: '12px', backgroundColor: '#f0fdf4' }}>
              <p style={{ margin: '0', fontSize: '10px', color: '#15803d', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Valid Until</p>
              <p style={{ margin: '6px 0 0 0', fontSize: '13px', fontWeight: '700' }}>{validUntil}</p>
            </div>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: '12px', padding: '12px', backgroundColor: '#f8fafc' }}>
              <p style={{ margin: '0', fontSize: '10px', color: '#475569', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Project Type</p>
              <p style={{ margin: '6px 0 0 0', fontSize: '13px', fontWeight: '700' }}>{estimate.project_type || 'N/A'}</p>
            </div>
            <div style={{ border: '1px solid #ffedd5', borderRadius: '12px', padding: '12px', backgroundColor: '#fff7ed' }}>
              <p style={{ margin: '0', fontSize: '10px', color: '#c2410c', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Timeline</p>
              <p style={{ margin: '6px 0 0 0', fontSize: '13px', fontWeight: '700' }}>{timelineText}</p>
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', letterSpacing: '0.4px', textTransform: 'uppercase', color: '#475569' }}>Project Overview</h3>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: '12px', padding: '14px 16px', backgroundColor: '#ffffff' }}>
              <p style={{ margin: '0 0 6px 0', fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>{estimate.project_name || 'Untitled Project'}</p>
              <p style={{ margin: '0', fontSize: '12px', color: '#475569', whiteSpace: 'pre-wrap' }}>{estimate.scope_of_work || 'Scope of work is not provided for this estimate.'}</p>
            </div>
          </div>

          <div style={{ border: '1px solid #cbd5e1', borderRadius: '12px', overflow: 'hidden', marginBottom: '20px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#0f172a', color: '#f8fafc' }}>
                  <th style={{ padding: '12px 14px', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Description</th>
                  <th style={{ padding: '12px 14px', textAlign: 'center', width: '70px', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Qty</th>
                  <th style={{ padding: '12px 14px', textAlign: 'right', width: '120px', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Unit Rate</th>
                  <th style={{ padding: '12px 14px', textAlign: 'right', width: '120px', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Line Total</th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((item, index) => {
                  const qty = parseFloat(item.quantity) || 0;
                  const rate = parseFloat(item.unit_rate) || 0;
                  const amount = qty * rate;

                  return (
                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#f8fafc' : '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '11px 14px', fontSize: '12px', color: '#0f172a' }}>{item.description || 'Untitled item'}</td>
                      <td style={{ padding: '11px 14px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#1e293b' }}>{qty}</td>
                      <td style={{ padding: '11px 14px', textAlign: 'right', fontSize: '12px', color: '#1e3a8a', fontWeight: '600' }}>{formatCurrency(rate)}</td>
                      <td style={{ padding: '11px 14px', textAlign: 'right', fontSize: '12px', color: '#0f172a', fontWeight: '700' }}>{formatCurrency(amount)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '20px' }}>
            <div style={{ flex: 1 }}>
              <p style={{ margin: '0 0 6px 0', fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '700' }}>Commercial Terms</p>
              <p style={{ margin: '0 0 3px 0', fontSize: '12px', color: '#334155' }}><strong>Payment:</strong> {estimate.payment_terms || 'As per agreement'}</p>
              <p style={{ margin: '0 0 3px 0', fontSize: '12px', color: '#334155' }}><strong>Method:</strong> {estimate.payment_method || 'N/A'}</p>
              {estimate.special_notes && (
                <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#475569', whiteSpace: 'pre-wrap' }}><strong>Notes:</strong> {estimate.special_notes}</p>
              )}
            </div>

            <div style={{ minWidth: '290px', border: '1px solid #dbeafe', borderRadius: '14px', padding: '14px', backgroundColor: '#f8fbff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '8px', color: '#334155' }}>
                <span>Subtotal</span>
                <strong>{formatCurrency(subtotal)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '8px', color: '#334155' }}>
                <span>Tax ({estimate.tax_percentage || 0}%)</span>
                <strong>{formatCurrency(taxAmount)}</strong>
              </div>
              {discountAmount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '10px', color: '#166534' }}>
                  <span>Discount</span>
                  <strong>-{formatCurrency(discountAmount)}</strong>
                </div>
              )}
              <div style={{ borderTop: '1px solid #bfdbfe', paddingTop: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', fontWeight: '800', color: '#0f172a', letterSpacing: '0.4px' }}>TOTAL ESTIMATE</span>
                <span style={{ fontSize: '18px', fontWeight: '800', color: '#1d4ed8' }}>{formatCurrency(total)}</span>
              </div>
            </div>
          </div>

          <div style={{ marginTop: '24px', borderTop: '1px solid #e2e8f0', paddingTop: '14px', textAlign: 'center' }}>
            <p style={{ margin: '0', fontSize: '11px', color: '#64748b' }}>This estimate is valid until {validUntil}. Thank you for the opportunity to work together.</p>
          </div>
        </div>
      </div>
    </div>
  );
});

EstimateTemplate.displayName = 'EstimateTemplate';

export default EstimateTemplate;
